// Pure formatting/validation helpers for charity-care form inputs. Kept framework-free (no Stimulus)
// so they're unit-testable and importable by input_format_controller.js. Mirrors the server-side
// CharityCareApplicationField#normalize_input_value so the two agree.
//
// The guiding invariant (matching the server): we ONLY rewrite a value when we're confident about
// its shape. Prefilled or non-conforming values are left untouched — never truncated or blanked.

export const STATE_NAME_TO_ABBREVIATION = {
  "alabama": "AL", "alaska": "AK", "arizona": "AZ", "arkansas": "AR",
  "california": "CA", "colorado": "CO", "connecticut": "CT", "delaware": "DE",
  "florida": "FL", "georgia": "GA", "hawaii": "HI", "idaho": "ID",
  "illinois": "IL", "indiana": "IN", "iowa": "IA", "kansas": "KS",
  "kentucky": "KY", "louisiana": "LA", "maine": "ME", "maryland": "MD",
  "massachusetts": "MA", "michigan": "MI", "minnesota": "MN", "mississippi": "MS",
  "missouri": "MO", "montana": "MT", "nebraska": "NE", "nevada": "NV",
  "new hampshire": "NH", "new jersey": "NJ", "new mexico": "NM", "new york": "NY",
  "north carolina": "NC", "north dakota": "ND", "ohio": "OH", "oklahoma": "OK",
  "oregon": "OR", "pennsylvania": "PA", "rhode island": "RI", "south carolina": "SC",
  "south dakota": "SD", "tennessee": "TN", "texas": "TX", "utah": "UT",
  "vermont": "VT", "virginia": "VA", "washington": "WA", "west virginia": "WV",
  "wisconsin": "WI", "wyoming": "WY", "district of columbia": "DC",
  "puerto rico": "PR", "virgin islands": "VI", "guam": "GU",
  "american samoa": "AS", "northern mariana islands": "MP",
}

export const VALID_STATE_ABBREVIATIONS = new Set(Object.values(STATE_NAME_TO_ABBREVIATION))

function digitsOnly(value) {
  return String(value ?? "").replace(/\D/g, "")
}

// A value is only eligible for numeric normalization if it's made up entirely of digits and allowed
// separators/whitespace (phone also permits a single leading "+"). Stricter than counting digits, so
// a value with letters/suffix ("98101ABC", "123-45-6789x", "2065551234 ext") or a misplaced/repeated
// "+" ("206+5551234", "++12065551234") is left untouched.
const PHONE_SHAPE = /^\+?[\d\s().-]+$/
const DIGITS_AND_SEPARATORS = /^[\d\s-]+$/

// Digit count of a confidently US-shaped phone, or null if the value isn't phone-shaped.
function usPhoneDigits(value) {
  const str = String(value ?? "")
  if (!PHONE_SHAPE.test(str)) return null
  const d = digitsOnly(str)
  if (d.length === 10 || (d.length === 11 && d[0] === "1")) return d
  return null
}

// Progressive phone mask for a value the member is actively typing. Caps at 10 digits (dropping a
// leading US country code) and adds separators as they go.
export function formatPhone(value) {
  let d = digitsOnly(value).slice(0, 11)
  if (d.length === 11 && d[0] === "1") d = d.slice(1)
  d = d.slice(0, 10)
  if (d.length === 0) return ""
  if (d.length < 4) return `(${d}`
  if (d.length < 7) return `(${d.slice(0, 3)}) ${d.slice(3)}`
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`
}

export function formatSsn(value) {
  const d = digitsOnly(value).slice(0, 9)
  if (d.length <= 3) return d
  if (d.length <= 5) return `${d.slice(0, 3)}-${d.slice(3)}`
  return `${d.slice(0, 3)}-${d.slice(3, 5)}-${d.slice(5)}`
}

export function formatZip(value) {
  const d = digitsOnly(value).slice(0, 9)
  if (d.length <= 5) return d
  return `${d.slice(0, 5)}-${d.slice(5)}`
}

// Standardize a state to its 2-letter USPS code: map a recognized full name, uppercase a recognized
// abbreviation, otherwise return the ORIGINAL value untouched (never a wrong/partial code).
export function normalizeState(value) {
  const raw = String(value ?? "")
  const trimmed = raw.trim()
  if (trimmed === "") return raw

  const byName = STATE_NAME_TO_ABBREVIATION[trimmed.toLowerCase().replace(/\s+/g, " ")]
  if (byName) return byName

  const upper = trimmed.toUpperCase()
  if (VALID_STATE_ABBREVIATIONS.has(upper)) return upper

  return raw
}

// Live masking as the member types. Lossless: only values still confidently compatible with the
// US format are progressively masked. A value with incompatible characters (a leading "+", letters,
// an alphanumeric postal code) or more digits than the format allows is passed through unchanged so
// we never strip or truncate what the member typed. `state` only uppercases here (full-name ->
// abbrev mapping happens on blur, once they've finished typing, so we don't fight mid-word input).
export function maskForInput(kind, value) {
  const str = String(value ?? "")

  switch (kind) {
    case "phone": {
      // No leading "+" (that's an international/E.164 entry) and at most a 10-digit US number
      // (a leading country-code 1 is allowed, i.e. up to 11 with a leading 1).
      if (!/^[\d\s().-]*$/.test(str)) return str
      const d = digitsOnly(str)
      const overlength = d.length > 11 || (d.length === 11 && d[0] !== "1")
      return overlength ? str : formatPhone(str)
    }
    case "ssn": {
      if (!DIGITS_AND_SEPARATORS.test(str) && str !== "") return str
      return digitsOnly(str).length > 9 ? str : formatSsn(str)
    }
    case "zip": {
      if (!DIGITS_AND_SEPARATORS.test(str) && str !== "") return str
      const d = digitsOnly(str)
      // Don't inject a ZIP+4 hyphen into an intermediate 6-8 digit value (e.g. a 6-digit non-US
      // postal code like "560001"); only a complete 9-digit ZIP+4 gets hyphenated.
      if (d.length > 9 || (d.length > 5 && d.length < 9)) return str
      return formatZip(str)
    }
    case "state":
      return str.toUpperCase()
    default:
      return value
  }
}

// Conservative normalization for a prefilled/server-rendered value on connect. Only canonicalizes a
// confidently-recognized value; anything else (partial, international, extended, non-numeric) is
// returned untouched so loading + submitting a form can never corrupt or blank an existing value.
export function normalizePrefilled(kind, value) {
  const raw = String(value ?? "")
  if (raw.trim() === "") return raw

  switch (kind) {
    case "phone":
      return usPhoneDigits(raw) ? formatPhone(raw) : raw
    case "ssn": {
      if (!DIGITS_AND_SEPARATORS.test(raw)) return raw
      return digitsOnly(raw).length === 9 ? formatSsn(raw) : raw
    }
    case "zip": {
      if (!DIGITS_AND_SEPARATORS.test(raw)) return raw
      const len = digitsOnly(raw).length
      return (len === 5 || len === 9) ? formatZip(raw) : raw
    }
    case "state":
      return normalizeState(raw)
    default:
      return raw
  }
}
